/**
 * Range export to text/Markdown (the data belongs to the user).
 * Reads directly from the local DB at export time (no network).
 */
import { createEffect, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Download, Loader2 } from "@fleet/icons";
import {
  addDaysToKey,
  entriesToMarkdown,
  entriesToPlainText,
  type ExportableEntry,
  getAllEntryTagsQuery,
  getEntriesInRangeQuery,
  getUserPhotosQuery,
  isDateKey,
  journalExportFilename,
  localDateKey,
} from "@journal/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "../hooks/use-journal-data";

const DEFAULT_RANGE_DAYS = -30;

function download(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ExportDialog() {
  const { t } = useI18n();
  const { drizzle, userId } = useJournalData();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/stats" });

  const [open, setOpen] = createSignal(search().export === true);
  const [start, setStart] = createSignal(
    search().from ?? addDaysToKey(localDateKey(), DEFAULT_RANGE_DAYS),
  );
  const [end, setEnd] = createSignal(search().to ?? localDateKey());
  const [format, setFormat] = createSignal<"md" | "txt">(search().fmt ?? "md");
  const [working, setWorking] = createSignal(false);

  // URL → state: the export dialog is URL-addressable
  // (`/stats?export=1&from=&to=&fmt=`). Reloading or pasting the URL reopens the
  // dialog with the same range and format. Idempotent.
  createEffect(() => {
    const p = search();
    setOpen(p.export === true);
    if (p.from) {
      setStart(p.from);
    }
    if (p.to) {
      setEnd(p.to);
    }
    if (p.fmt) {
      setFormat(p.fmt);
    }
  });

  // state → URL: writes idempotent params (omitting the default range and
  // the `md` format for clean URLs). `replace` avoids bloating history.
  const syncUrl = (next: { open: boolean; start: string; end: string; format: "md" | "txt" }) => {
    const defaultStart = addDaysToKey(localDateKey(), DEFAULT_RANGE_DAYS);
    void navigate({
      to: "/stats",
      search: {
        export: next.open ? true : undefined,
        from: next.open && next.start !== defaultStart ? next.start : undefined,
        to: next.open && next.end !== localDateKey() ? next.end : undefined,
        fmt: next.open && next.format !== "md" ? next.format : undefined,
      },
      replace: true,
    });
  };

  const setOpenUrl = (value: boolean) => {
    setOpen(value);
    syncUrl({ open: value, start: start(), end: end(), format: format() });
  };
  const setStartUrl = (value: string) => {
    setStart(value);
    syncUrl({ open: true, start: value, end: end(), format: format() });
  };
  const setEndUrl = (value: string) => {
    setEnd(value);
    syncUrl({ open: true, start: start(), end: value, format: format() });
  };
  const setFormatUrl = (value: "md" | "txt") => {
    setFormat(value);
    syncUrl({ open: true, start: start(), end: end(), format: value });
  };

  const handleExport = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid || working()) {
      return;
    }
    if (!isDateKey(start()) || !isDateKey(end()) || start() > end()) {
      showToast(t(msg`Pick a valid date range.`), "error");
      return;
    }
    setWorking(true);
    try {
      const [entries, tags, photos] = await Promise.all([
        getEntriesInRangeQuery(db, uid, start(), end()),
        getAllEntryTagsQuery(db, uid),
        getUserPhotosQuery(db, uid),
      ]);

      const tagsByEntry = new Map<string, string[]>();
      for (const row of tags) {
        if (!row.entry_id || !row.name) {
          continue;
        }
        tagsByEntry.set(row.entry_id, [...(tagsByEntry.get(row.entry_id) ?? []), row.name]);
      }
      const photosByEntry = new Map<string, string[]>();
      for (const photo of photos) {
        if (!photo.entry_id) {
          continue;
        }
        photosByEntry.set(photo.entry_id, [
          ...(photosByEntry.get(photo.entry_id) ?? []),
          photo.filename ?? "photo",
        ]);
      }

      const exportable: ExportableEntry[] = entries.map((entry) => ({
        entry_date: entry.entry_date,
        title: entry.title,
        body: entry.body,
        mood: entry.mood,
        created_at: entry.created_at,
        tags: tagsByEntry.get(entry.id) ?? [],
        photos: photosByEntry.get(entry.id) ?? [],
      }));

      if (exportable.length === 0) {
        showToast(t(msg`No entries in that range.`), "info");
        return;
      }

      const isMarkdown = format() === "md";
      const content = isMarkdown ? entriesToMarkdown(exportable) : entriesToPlainText(exportable);
      download(
        content,
        journalExportFilename(start(), end(), format()),
        isMarkdown ? "text/markdown" : "text/plain",
      );
      setOpenUrl(false);
      showToast(t(msg`Export ready.`), "success");
    } catch (error) {
      console.error("Error exporting journal:", error);
      showToast(t(msg`Export failed. Please try again.`), "error");
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5"
        data-testid="export-open"
        onClick={() => setOpenUrl(true)}
      >
        <Download class="h-4 w-4" />
        {t(msg`Export`)}
      </Button>

      <Dialog open={open()} onOpenChange={setOpenUrl}>
        <div class="flex flex-col gap-4 p-1">
          <div>
            <DialogTitle>{t(msg`Export your journal`)}</DialogTitle>
            <DialogDescription>
              {t(msg`Your words belong to you. Download them as Markdown or plain text.`)}
            </DialogDescription>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <label class="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t(msg`From`)}
              <input
                type="date"
                data-testid="export-start"
                class="h-9 rounded-lg border border-border/50 bg-muted/30 px-2.5 text-sm text-foreground outline-none focus:border-primary/40"
                value={start()}
                max={end()}
                onInput={(e) => setStartUrl(e.currentTarget.value)}
              />
            </label>
            <label class="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t(msg`To`)}
              <input
                type="date"
                data-testid="export-end"
                class="h-9 rounded-lg border border-border/50 bg-muted/30 px-2.5 text-sm text-foreground outline-none focus:border-primary/40"
                value={end()}
                max={localDateKey()}
                onInput={(e) => setEndUrl(e.currentTarget.value)}
              />
            </label>
          </div>

          <div class="flex items-center gap-2">
            <Button
              variant={format() === "md" ? "default" : "outline"}
              size="sm"
              onClick={() => setFormatUrl("md")}
            >
              Markdown
            </Button>
            <Button
              variant={format() === "txt" ? "default" : "outline"}
              size="sm"
              onClick={() => setFormatUrl("txt")}
            >
              {t(msg`Plain text`)}
            </Button>
          </div>

          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpenUrl(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              size="sm"
              data-testid="export-download"
              disabled={working() || !drizzle()}
              onClick={() => {
                void handleExport();
              }}
            >
              <Show when={working()}>
                <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
              </Show>
              {t(msg`Download`)}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
